import { Theme } from '@/entities/settings/models/types';

/** Ключ localStorage, под которым стор настроек держит свои глобальные поля */
export const SETTINGS_STORAGE_KEY = 'global-settings';

/** Версия localStorage-персистенции стора настроек */
export const SETTINGS_STORAGE_VERSION = 2;

/**
 * Версия хранилища, начиная с которой сохранённая тема — это выбор пользователя.
 * Записи более ранних версий не берутся: в них могло попасть оформление устройства, а не выбор,
 * и отличить одно от другого в сохранённом значении нельзя. Дальнейшие повышения
 * {@link SETTINGS_STORAGE_VERSION} по другим поводам тему обесценивать не должны, поэтому
 * граница отдельная и повышается только вместе со следующей порчей темы.
 */
export const THEME_TRUSTED_SINCE_VERSION = 2;

/** Тема, которая действует, пока пользователь не выбрал другую */
export const DEFAULT_THEME: Theme = 'light';

/**
 * Применяет тему к документу, добавляя или удаляя класс 'dark' на корневом элементе
 */
export const applyThemeToDocument = (theme: Theme): void => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

/**
 * Читает тему, применённую на этом устройстве последней. Запись версии старше
 * {@link THEME_TRUSTED_SINCE_VERSION} не берётся — выбором пользователя она не является.
 */
const readDeviceTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { version?: number; state?: { theme?: unknown } };
    if (typeof parsed?.version !== 'number' || parsed.version < THEME_TRUSTED_SINCE_VERSION) return null;
    const theme = parsed?.state?.theme;
    return typeof theme === 'string' && theme ? theme : null;
  } catch {
    return null;
  }
};

/**
 * Возвращает тему, в которой интерфейс показывается до получения личных настроек
 * (экран входа, начальная загрузка): применённую на устройстве последней, иначе — умолчание.
 * Оформление устройства и браузера темой не управляет. Тему сразу накладывает на документ.
 */
export const getInitialTheme = (): Theme => {
  const theme = readDeviceTheme() ?? DEFAULT_THEME;
  applyThemeToDocument(theme);
  return theme;
};
